import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { HardHat, TrendingUp, AlertTriangle, ClipboardList, Building, BarChart3, ArrowRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = !ENV_BACKEND_URL || ENV_BACKEND_URL === '' ? window.location.origin : ENV_BACKEND_URL;
if (BACKEND_URL.endsWith('/api')) BACKEND_URL = BACKEND_URL.slice(0, -4);
const API = `${BACKEND_URL}/api`;

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function ConstructionDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/construction/dashboard-stats`);
      setStats(res.data);
    } catch (error) {
      toast.error('Error al cargar indicadores de construcción');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket Integration
  const { socket } = require('../contexts/AuthContext').useAuth();
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'CONSTRUCTION_UPDATE' || message.type === 'INVENTORY_UPDATE') {
          console.log('Real-time update received:', message.type);
          fetchStats();
        }
      } catch (e) {
        console.error('Error parsing WS message:', e);
      }
    };

    socket.addEventListener('message', handleMessage);
    return () => socket.removeEventListener('message', handleMessage);
  }, [socket]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground font-medium animate-pulse">Cargando indicadores...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight flex items-center gap-3">
            <HardHat className="h-10 w-10 text-indigo-600" />
            Panel Construcción
          </h1>
          <p className="text-muted-foreground mt-2">Indicadores clave de proyectos y obras en curso</p>
        </div>
        <Button asChild className="hidden md:flex">
          <Link to="/projects">Gestionar Obras <ArrowRight className="ml-2 h-4 w-4" /></Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-lg transition-all border-l-4 border-l-indigo-600">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Obras Activas</CardTitle>
            <Building className="h-5 w-5 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold tracking-tight">{stats?.active_projects || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Proyectos en ejecución</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all border-l-4 border-l-emerald-600">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Presupuesto Total</CardTitle>
            <TrendingUp className="h-5 w-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight">{formatCLP(stats?.total_budget || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Monto acumulado de obras</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all border-l-4 border-l-orange-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Retenciones Acum.</CardTitle>
            <BarChart3 className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight">{formatCLP(stats?.total_retentions || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Garantías por cobrar (Estados de Pago)</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-all border-l-4 border-l-red-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Solicitudes Pendientes</CardTitle>
            <ClipboardList className="h-5 w-5 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold tracking-tight">{stats?.pending_requisitions || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Solicitudes por procesar</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 flex flex-col h-full">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-400" /> 
                Alertas y Notificaciones
            </h3>
            <div className="flex-1 flex flex-col justify-center items-center text-center space-y-3 p-8 border-2 border-dashed rounded-xl bg-muted/5">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-full">
                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                    <p className="font-bold text-emerald-800 dark:text-emerald-300">Todo en orden</p>
                    <p className="text-sm text-muted-foreground">No hay desviaciones presupuestarias ni estados de pago vencidos por ahora.</p>
                </div>
            </div>
        </Card>

        <Card className="p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Building className="h-5 w-5 text-indigo-400" /> 
                Accesos Directos
            </h3>
            <div className="grid grid-cols-2 gap-4">
                <Link to="/projects" className="p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors flex flex-col items-center text-center gap-2">
                    <Building className="h-8 w-8 text-indigo-500" />
                    <span className="font-semibold">Obras</span>
                </Link>
                <Link to="/requisitions" className="p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors flex flex-col items-center text-center gap-2">
                    <ClipboardList className="h-8 w-8 text-emerald-500" />
                    <span className="font-semibold">Solicitudes</span>
                </Link>
                <Link to="/subcontracts" className="p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors flex flex-col items-center text-center gap-2">
                    <FileText className="h-8 w-8 text-blue-500" />
                    <span className="font-semibold">Subcontratos</span>
                </Link>
                <Link to="/machinery" className="p-4 rounded-xl border border-border hover:bg-muted/50 transition-colors flex flex-col items-center text-center gap-2">
                    <HardHat className="h-8 w-8 text-orange-500" />
                    <span className="font-semibold">Maquinaria</span>
                </Link>
            </div>
        </Card>
      </div>
    </div>
  );
}
